/**
 * The send engine (FEAT-038 wave 2) — the ONE path anything with media takes
 * to leave this phone.
 *
 * It replaces five services (`sendOutbox`, `chunkedUpload`, `stagedUpload`,
 * `uploadSessionStore`, `stagedSend`) and the dual "POST first, queue if that
 * fails" shape they were wired into. That shape is what BUG-119 was: a send's
 * destination was only written to disk AFTER a direct POST had failed, so a
 * send interrupted in between became a moment with no recipient. Here the
 * ledger row — recipient included — is written at send-tap, before a byte
 * moves, and there is no other way to send.
 *
 * The rules it keeps, each paid for by a bug:
 *
 * - **Constant memory.** An upload reads one ~2 MiB chunk, PATCHes it, records
 *   the new offset and reads the next. A 400 MB clip costs what a 4 MB one
 *   does. Nothing here ever holds a file.
 * - **The server's offset wins.** A resume asks, adopts what it is told —
 *   including the offset inside a 409 OFFSET_MISMATCH — and only ever starts
 *   over on a 404. Nine sessions for six clips, all abandoned near zero, is
 *   what the old code did instead.
 * - **Finalize is deferred.** Finalizing mints the staged row whose one-hour
 *   reaper then starts ticking, so it happens when a destination POST is about
 *   to follow — never at the end of an upload that may then wait for signal.
 * - **429 is pacing, not failure.** It costs a wait, never a retry, and a
 *   client-side bucket mirrors the server's own limits so we mostly don't ask.
 * - **401 pauses, visibly.** Signed out is not refused: the rows keep their
 *   place, the pill says a sign-in is needed, and signing in resumes them.
 * - **Disk before action.** Every state change is persisted before it is acted
 *   on, and a ledger write that fails stops the action it was recording.
 */
import { evacuation } from '../media/evacuate';
import { mediaStore, type MediaMeta } from '../media/mediaStore';
import { drafts } from '../media/drafts';
import { ledger } from './ledger';
import { StreamingDigest, blobSource, mediaSource, type ChunkSource } from './bytes';
import { runQueueMigration, legacyOutboxBlob } from './migrate';
import {
  clientKeyFor,
  type QueueRow,
  type SendIntent,
  type SendResult,
  type SendState,
  type SessionRow,
} from './types';
import {
  CHUNK_THRESHOLD_BYTES,
  STUCK_AFTER_FAILURES,
  computeChunkTimeoutMs,
  computeUploadTimeoutMs,
  isPermanentStatus,
  retryDelayMs,
} from '../uploadPolicy';
import { MEDIA_CHUNK_BYTES } from '../media/mediaStore';
import { serializeOverlays } from '../../utils/overlayPayload';
import { uploadFileName } from '../../utils/uploadFileName';
import { API_BASE_URL, reportUnauthorized } from '../../api/client';

/**
 * The server reaps staged rows an hour after they are minted. Past this age we
 * upload again rather than hand a send an id that may already be gone — a
 * 404-loop costs a round trip per destination and still ends in a re-upload.
 */
const STALE_STAGED_MS = 50 * 60 * 1000;

/** Above this an upload is chunked and resumable; below it a single POST to
 * /media/staged is fewer round trips than the handshake would cost. */
const HEAVY_BYTES = CHUNK_THRESHOLD_BYTES;

/** Server-side buckets we must not walk into (backend/app/routes/media.py):
 * session-create, finalize and /media/staged all share `upload`; chunk appends
 * have their own, far larger, allowance. */
const UPLOAD_PER_MIN = 20;
const CHUNK_PER_MIN = 240;

class SendError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: string,
    readonly permanent = false
  ) {
    super(message);
  }
}

/** A staged id the server no longer has. Not a failure — the bytes are still
 * on this phone, so the upload simply happens again. */
class StagedGoneError extends Error {}

interface Reply<T> {
  ok: boolean;
  status: number;
  data?: T;
  code?: string;
  message?: string;
  /** 409 OFFSET_MISMATCH carries the offset the server actually holds. */
  offset?: number;
  retryAfterMs?: number;
}

/** A sliding-window allowance that mirrors one of the server's rate limits, so
 * the normal case never produces a 429 at all. */
class TokenBucket {
  private stamps: number[] = [];
  constructor(
    private readonly limit: number,
    private readonly windowMs = 60_000
  ) {}

  async take(): Promise<void> {
    for (;;) {
      const now = Date.now();
      this.stamps = this.stamps.filter((t) => now - t < this.windowMs);
      if (this.stamps.length < this.limit) {
        this.stamps.push(now);
        return;
      }
      const wait = this.windowMs - (now - this.stamps[0]) + 50;
      await delay(wait);
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Rows of one moment, in the order they were committed. The unit of work: the
 * media goes up ONCE and every destination of it claims or copies that one
 * upload. */
interface MediaJob {
  mediaId: string;
  rows: QueueRow[];
  size: number;
  createdAt: number;
}

/**
 * Everything an upload needs to know about a moment's bytes — and nothing about
 * where it is going.
 *
 * Wave 3 gave the upload a second caller: a head start, which has bytes and no
 * destination yet. Rather than a second copy of create-append (which is how the
 * five services this file replaced came to disagree with each other), the two
 * meet here: a real `MediaJob` and a head start each produce one of these, and
 * the create and append steps only ever read this.
 */
interface UploadTarget {
  mediaId: string;
  mediaType: 'photo' | 'video';
  hd: boolean;
  /** The blob's real MIME. The backend validates `content_type` at session
   * create, so the old 'application/octet-stream' fallback would 400. */
  mime: string;
  source: ChunkSource;
}

const isVault = (row: QueueRow): boolean => row.dest.kind === 'vault';

type Kind = 'send' | 'vault' | 'all';
const matchesKind = (row: QueueRow, kind: Kind): boolean =>
  kind === 'all' ? true : kind === 'vault' ? isVault(row) : !isVault(row);

export class SendEngineService {
  private ready: Promise<void> | null = null;
  private draining = false;
  private rerun = false;
  /** Medias a lane is working on, so the two lanes can never collide. */
  private inFlight = new Set<string>();
  /** Set by a 401; cleared by the first success of a later pass. */
  private needsAuth = false;
  /** The head start in flight, if any — held so a draft delete can wait for
   * the append to stop before cancelling the session under it. */
  private running: { mediaId: string; work: Promise<void> } | null = null;
  /** Medias whose head start is being torn down right now. */
  private abandoning = new Set<string>();
  /** Passes started and not yet finished. Only `idle()` reads it. */
  private passes = 0;
  /** Wall-clock the server told us to wait until (429 Retry-After). */
  private pacedUntil = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private uploadBucket = new TokenBucket(UPLOAD_PER_MIN);
  private chunkBucket = new TokenBucket(CHUNK_PER_MIN);
  private stateCallbacks = new Set<(state: SendState) => void>();
  private resultCallbacks = new Set<(result: SendResult) => void>();
  private state: SendState = {
    pending: 0,
    stuck: 0,
    offline: false,
    needsAuth: false,
    saving: 0,
    savingStuck: 0,
  };

  /**
   * Load the queue. METADATA ONLY: no network, no media bytes, no chunk reads —
   * the regression this whole campaign exists to prevent. Sending starts when
   * the app says someone is signed in (`kick()`), never as a side effect of
   * opening the app.
   */
  init(): Promise<void> {
    if (!this.ready) {
      this.ready = (async () => {
        await evacuation.done();
        await ledger.init();
        await runQueueMigration();
        this.needsAuth = ledger.all().some((row) => row.pausedAuth);
        this.listen();
        this.refreshState();
      })().catch((err) => {
        this.ready = null;
        throw err;
      });
    }
    return this.ready;
  }

  /**
   * Resolves once the ledger is loaded — the surfaces' "may I trust
   * `activeMediaIds()` / `rows()` yet?" signal. Before it resolves those
   * synchronous readers answer from an EMPTY mirror, which is how a queued
   * send got itself counted as an orphan draft at boot (BUG-122). Memoized
   * via init(); safe to await from any recompute.
   */
  whenReady(): Promise<void> {
    return this.init();
  }

  private listening = false;

  private listen(): void {
    if (this.listening || typeof window === 'undefined') return;
    this.listening = true;
    window.addEventListener('online', () => {
      this.refreshState();
      this.kick();
    });
    window.addEventListener('offline', () => this.refreshState());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.kick();
    });
  }

  // ---- the send-tap entry point ----

  /**
   * Commit one moment's destinations and return. Nothing is uploaded, claimed
   * or posted on this path: the ledger write IS the send, and everything after
   * it is the engine's problem. That is what makes a recipient survive a
   * force-quit two seconds later.
   */
  async enqueue(intents: SendIntent[]): Promise<QueueRow[]> {
    await this.init();
    const now = Date.now();
    const rows: QueueRow[] = [];
    for (const intent of intents) {
      const size =
        intent.size ?? (await mediaStore.meta(intent.mediaId))?.size ?? 0;
      for (const dest of intent.destinations) {
        const clientKey = clientKeyFor(intent.mediaId, dest);
        // Idempotent by construction: the same moment to the same place is one
        // row, whether it arrives from a double tap or a re-send.
        if (ledger.get(clientKey)) continue;
        rows.push({
          id: clientKey,
          mediaId: intent.mediaId,
          clientKey,
          dest,
          state: 'queued',
          size,
          mediaType: intent.mediaType,
          caption: intent.caption,
          overlays: intent.overlays ?? null,
          retentionType: intent.retentionType,
          replayAllowed: intent.replayAllowed,
          hd: intent.hd,
          facing: intent.facing,
          savedItemId: intent.savedItemId,
          draftId: intent.draftId,
          optimisticId: intent.optimisticIds?.[clientKey],
          createdAt: now,
          updatedAt: now,
          attempts: 0,
        });
      }
    }
    // ONE transaction: a moment going to four places is committed for all four
    // or for none. A half-written fan-out is a moment that sends to some of the
    // people you picked and silently forgets the rest.
    await ledger.putMany(rows);
    this.refreshState();
    this.kick();
    return rows;
  }

  // ---- the drain ----

  /** Ask for a pass. Cheap and idempotent — a pass already running just gets
   * asked to run again when it finishes, so a row added mid-pass is never
   * dropped (BUG-055's silent trigger loss). */
  kick(): void {
    this.passes++;
    void this.drain().finally(() => {
      this.passes--;
    });
  }

  /**
   * Resolves when nothing is in flight — passes, and the head start.
   *
   * Tests only, and designed in rather than retrofitted: the suite's `settle()`
   * watches the LEDGER, and a head start deliberately leaves no ledger row, so
   * every assertion about one would otherwise race the upload it is about.
   */
  async idle(): Promise<void> {
    for (let i = 0; i < 2_000; i++) {
      if (this.passes === 0 && !this.running) return;
      await delay(5);
    }
  }

  private async drain(): Promise<void> {
    await this.init();
    if (this.draining) {
      this.rerun = true;
      return;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.refreshState();
      return;
    }
    this.draining = true;
    // Every pass starts optimistic: if the session came back, the first success
    // clears "sign in to finish sending" by itself.
    this.needsAuth = false;
    try {
      await this.withLock(async () => {
        // Two lanes. A serial heavy lane so one big clip has the uplink to
        // itself, and a light lane beside it so a photo can never wait behind
        // a video (BUG-055's starvation, made structural).
        await Promise.all([this.lane('heavy'), this.lane('light')]);
        // And a third, only ever HERE: both real lanes have run out of work,
        // so the head start inherits everything they own — the Web Lock, both
        // token buckets, the 401 pause, the offline check and the retry timer
        // — instead of being a second mechanism that has to remember them.
        await this.preUploadLane();
      });
    } catch (err) {
      console.error('[Queue] pass failed:', err);
    } finally {
      this.draining = false;
      this.inFlight.clear();
      this.refreshState();
      this.scheduleRetry();
      if (this.rerun) {
        this.rerun = false;
        this.passes++;
        void this.drain().finally(() => {
          this.passes--;
        });
      }
    }
  }

  /** Exactly one engine across tabs and service-worker wakeups. Without the
   * lock two copies would create two sessions for one clip and race each
   * other's offsets. */
  private async withLock(run: () => Promise<void>): Promise<void> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks?.request) {
      await run();
      return;
    }
    await locks.request(
      'momento-send-engine',
      { ifAvailable: true },
      async (lock) => {
        if (!lock) {
          console.info('[Queue] another tab holds the engine — skipping this pass');
          return;
        }
        await run();
      }
    );
  }

  private async lane(kind: 'heavy' | 'light'): Promise<void> {
    for (;;) {
      if (this.needsAuth) return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      const job = this.nextJob(kind);
      if (!job) return;
      this.inFlight.add(job.mediaId);
      try {
        await this.runMedia(job);
      } finally {
        this.inFlight.delete(job.mediaId);
      }
    }
  }

  /** The oldest moment this lane may work on. Ordering is capture order — the
   * order he took them in is the order they should land. */
  private nextJob(kind: 'heavy' | 'light'): MediaJob | null {
    const now = Date.now();
    const byMedia = new Map<string, QueueRow[]>();
    for (const row of ledger.all()) {
      if (row.state === 'stuck' || row.state === 'done') continue;
      if (row.nextAttemptAt && row.nextAttemptAt > now) continue;
      if (this.inFlight.has(row.mediaId)) continue;
      const list = byMedia.get(row.mediaId);
      if (list) list.push(row);
      else byMedia.set(row.mediaId, [row]);
    }
    const jobs: MediaJob[] = [];
    for (const [mediaId, rows] of byMedia) {
      const size = rows[0].size;
      // A Memory re-share moves no bytes at all, so it belongs in the light
      // lane however big the Memory is.
      const heavy = size >= HEAVY_BYTES && !rows[0].savedItemId;
      if (heavy !== (kind === 'heavy')) continue;
      rows.sort((a, b) => a.createdAt - b.createdAt);
      jobs.push({
        mediaId,
        rows,
        size,
        createdAt: Math.min(...rows.map((r) => r.createdAt)),
      });
    }
    jobs.sort((a, b) => a.createdAt - b.createdAt);
    return jobs[0] ?? null;
  }

  // ---- the head start ----

  /**
   * A clip's bytes start moving when the RECORDING stops, not when a recipient
   * is picked — the caption and the friend list are where the seconds actually
   * go, and by then a 60-second clip could already be most of the way up.
   *
   * **The one rule: a head start writes no ledger row, ever.** A row exists to
   * say where a moment is going; a row without a destination is BUG-119 built
   * again on purpose. The SESSION row is the whole record, and when the send
   * finally arrives, `prepareMedia` finds it, adopts the offset the server
   * confirmed, and carries on. Nothing new adopts it — that code already
   * existed for resuming after a force-quit.
   */
  async preUpload(mediaId: string, size: number): Promise<void> {
    // Chunked lane only. A small file's single POST would have to FINALIZE to
    // exist at all, and finalizing mints the staged row whose one-hour reaper
    // then starts ticking on a moment nobody has addressed yet. Sessions, by
    // contrast, live 24 h idle and every append refreshes them.
    if (size < HEAVY_BYTES) return;
    await this.init();
    if (await ledger.session(mediaId)) return;
    await ledger.putSession({
      mediaId,
      uploadId: null,
      committedOffset: 0,
      hashedBytes: 0,
      totalBytes: size,
      updatedAt: Date.now(),
    });
    this.kick();
  }

  /**
   * The user deleted the draft. Its head start goes with it — stopping the
   * append FIRST, because an abort that lands mid-PATCH answers 409
   * UPLOAD_CANCELLED, which nothing here special-cases and which would surface
   * as a mysterious failure on a moment the user had already thrown away.
   */
  async abandonUpload(mediaIds: string[]): Promise<void> {
    if (mediaIds.length === 0) return;
    await this.init();
    for (const id of mediaIds) this.abandoning.add(id);
    try {
      const running = this.running;
      if (running && mediaIds.includes(running.mediaId)) {
        await running.work.catch(() => {});
      }
      for (const mediaId of mediaIds) await this.abortSession(mediaId);
    } finally {
      for (const id of mediaIds) this.abandoning.delete(id);
    }
  }

  /** Tell the server to forget a partial upload, and drop its session row. */
  private async abortSession(mediaId: string): Promise<void> {
    const session = await ledger.session(mediaId);
    if (!session) return;
    if (session.uploadId) {
      await this.request('DELETE', `/media/uploads/${session.uploadId}`).catch(() => {});
    }
    await ledger.removeSession(mediaId);
  }

  /**
   * Sessions nobody's queue row owns: a draft's head start, or the litter of a
   * send that is over. The media tells them apart — abandonment is tied to the
   * DRAFT'S LIFE, not to a timer, so a clip sent hours later still gets its
   * head start and the server's own 24 h idle reaper is the only backstop.
   */
  private async preUploadLane(): Promise<void> {
    for (const mediaId of ledger.orphanSessionIds()) {
      if (this.needsAuth) return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      if (this.abandoning.has(mediaId)) continue;
      const meta = await mediaStore.meta(mediaId);
      if (!meta) {
        // The moment it belonged to is gone; its `.part` on the server is
        // litter. A session whose draft still LIVES is never swept — it is
        // that draft's progress.
        await this.abortSession(mediaId);
        continue;
      }
      if (!meta.draft || meta.size < HEAVY_BYTES) continue;
      // Real sends first, always: a moment with a recipient outranks one that
      // has not been addressed yet.
      if (this.hasLiveRows()) return;
      const work = this.headStart(meta);
      this.running = { mediaId, work };
      try {
        await work;
      } catch (err) {
        // There is no row to mark, and nothing was promised — a head start
        // that fails costs the send nothing but the head start. The next pass
        // tries again from the offset the server confirmed.
        if (err instanceof SendError && err.status === 401) this.needsAuth = true;
        console.warn('[Queue] head start stopped:', (err as Error).message);
      } finally {
        this.running = null;
      }
    }
  }

  /** Create-and-append for a moment with no destination yet. It NEVER
   * finalizes: finalize stays welded to the destination POST. */
  private async headStart(meta: MediaMeta): Promise<void> {
    const mediaId = meta.id;
    const stored = await ledger.session(mediaId);
    if (!stored || stored.stagedId) return;
    const target: UploadTarget = {
      mediaId,
      mediaType: meta.mediaType,
      hd: meta.hd ?? false,
      mime: meta.mimeType || (meta.mediaType === 'video' ? 'video/mp4' : 'image/jpeg'),
      source: mediaSource(mediaId, meta.size),
    };
    const opened = await this.openSession(target, { ...stored, totalBytes: meta.size });
    if (opened.stagedId) return;
    const digestRef = { digest: await this.digestFor(opened.session, target.source) };
    // Yield between chunks: the rerun flag re-drains, the new row's own job
    // adopts this very session at its committed offset, and it simply
    // continues. No pause/resume protocol, because there is nothing to resume.
    await this.pumpChunks(
      target,
      opened.session,
      digestRef,
      () => this.abandoning.has(mediaId) || this.hasLiveRows()
    );
  }

  /** Anything the real lanes still owe an answer for. */
  private hasLiveRows(): boolean {
    return ledger.all().some((row) => row.state !== 'stuck' && row.state !== 'done');
  }

  // ---- one moment, all its destinations ----

  private async runMedia(job: MediaJob): Promise<void> {
    try {
      let stagedId: string | null = null;
      if (!job.rows[0].savedItemId) {
        stagedId = await this.prepareMedia(job);
      }
      await this.deliver(job, stagedId);
    } catch (err) {
      await this.noteFailure(job, err);
    }
  }

  /**
   * Get this moment's bytes onto the server and return the staged id its
   * destinations will claim. Resumes whatever a previous launch left behind
   * and never re-reads what the server already has.
   */
  private async prepareMedia(job: MediaJob): Promise<string> {
    const source = await this.sourceFor(job);
    if (source.size !== job.size) {
      // The row was committed before its media finished being written, so its
      // size was a guess. Correct it now — the lanes read that number.
      for (const row of job.rows) await ledger.patch(row.id, { size: source.size });
      job.size = source.size;
    }
    let session = (await ledger.session(job.mediaId)) ?? {
      mediaId: job.mediaId,
      uploadId: null,
      committedOffset: 0,
      hashedBytes: 0,
      totalBytes: source.size,
      updatedAt: Date.now(),
    };

    if (session.stagedId) {
      if (Date.now() - (session.stagedAt ?? 0) < STALE_STAGED_MS) {
        return session.stagedId;
      }
      // Past the reaper's window the id is a liability, not a shortcut.
      console.warn('[Queue] staged upload aged out — sending the bytes again');
      session = { ...session, stagedId: undefined, stagedAt: undefined };
      await ledger.removeSession(job.mediaId);
      session.uploadId = null;
      session.committedOffset = 0;
      session.hashedBytes = 0;
      session.hashState = undefined;
    }

    if (source.size < HEAVY_BYTES) {
      return this.stageWhole(job, source, session);
    }
    return this.stageChunked(job, source, session);
  }

  /** Photos and small clips: one POST, no handshake. */
  private async stageWhole(
    job: MediaJob,
    source: ChunkSource,
    session: SessionRow
  ): Promise<string> {
    await this.setState(job.rows, 'uploading');
    const blob = await this.wholeBlob(job, source);
    const form = new FormData();
    form.append('media', blob, uploadFileName(blob, job.rows[0].mediaType));
    form.append('media_type', job.rows[0].mediaType);
    if (job.rows[0].hd) form.append('hd', 'true');

    await this.uploadBucket.take();
    const reply = await this.request<{ staged_id: string }>(
      'POST',
      '/media/staged',
      { body: form, timeoutMs: computeUploadTimeoutMs(source.size) }
    );
    this.assertOk(reply);
    const stagedId = reply.data!.staged_id;
    await ledger.putSession({
      ...session,
      stagedId,
      stagedAt: Date.now(),
      committedOffset: source.size,
      totalBytes: source.size,
    });
    await this.setState(job.rows, 'uploaded');
    return stagedId;
  }

  /**
   * Adopt or create the server-side session. Creation is LAZY — a queued clip
   * with no signal has no server session at all, which is the honest state.
   *
   * Returns a staged id only in the one case where the bytes turn out to be
   * finished already: a finalize whose answer we never heard.
   */
  private async openSession(
    target: UploadTarget,
    initial: SessionRow
  ): Promise<{ session: SessionRow; stagedId: string | null }> {
    let session = initial;
    if (session.uploadId) {
      const meta = await this.request<{
        offset: number;
        status: string;
        staged_id: string | null;
      }>('GET', `/media/uploads/${session.uploadId}`);
      if (meta.ok && meta.data) {
        if (meta.data.status === 'finalized' && meta.data.staged_id) {
          // Its staged row's age is unknown; a 404 at claim time re-uploads,
          // which is the right cost.
          session = {
            ...session,
            stagedId: meta.data.staged_id,
            stagedAt: Date.now(),
          };
          await ledger.putSession(session);
          return { session, stagedId: meta.data.staged_id };
        }
        session = { ...session, committedOffset: meta.data.offset };
      } else if (meta.status === 404) {
        // Reaped. The ONLY reason to abandon a session and start over.
        console.warn('[Queue] upload session gone — starting this clip again');
        session = {
          ...session,
          uploadId: null,
          committedOffset: 0,
          hashedBytes: 0,
          hashState: undefined,
        };
      } else {
        this.assertOk(meta);
      }
    }
    if (!session.uploadId) {
      await this.uploadBucket.take();
      const created = await this.request<{ upload_id: string }>(
        'POST',
        '/media/uploads',
        {
          json: {
            media_type: target.mediaType,
            hd: target.hd,
            content_type: target.mime,
            total_bytes: target.source.size,
          },
        }
      );
      this.assertOk(created);
      session = {
        ...session,
        uploadId: created.data!.upload_id,
        committedOffset: 0,
        hashedBytes: 0,
        hashState: undefined,
        totalBytes: target.source.size,
      };
      await ledger.putSession(session);
    }
    return { session, stagedId: null };
  }

  /** The checksum rides along with the upload, from the same reads. */
  private async digestFor(
    session: SessionRow,
    source: ChunkSource
  ): Promise<StreamingDigest> {
    let digest = await StreamingDigest.create(session);
    if (digest.hashedBytes > session.committedOffset) {
      // The server is behind our checkpoint (a session recreated under us) —
      // the only honest move is to hash again from the start.
      digest = await StreamingDigest.create();
    }
    await digest.catchUp(source, session.committedOffset);
    return digest;
  }

  /**
   * Append until the server has every byte — or until `stopEarly` says the
   * lane has something better to do (the head start yielding to a real send).
   *
   * The digest is fed the SAME body that just went up, so a chunk is never
   * read twice, and both the offset and the hasher's state are persisted
   * before the next read: a kill here costs the one chunk in flight, never the
   * ones behind it.
   */
  private async pumpChunks(
    target: UploadTarget,
    initial: SessionRow,
    digestRef: { digest: StreamingDigest },
    stopEarly?: () => boolean
  ): Promise<SessionRow> {
    const source = target.source;
    let session = initial;
    while (session.committedOffset < source.size) {
      if (stopEarly?.()) return session;
      const body = await source.readAt(session.committedOffset);
      if (!body || body.byteLength === 0) {
        throw new SendError('Media unreadable on this device', 410, 'MEDIA_GONE', true);
      }
      await this.chunkBucket.take();
      const patched = await this.request<{ offset: number }>(
        'PATCH',
        `/media/uploads/${session.uploadId}?offset=${session.committedOffset}`,
        {
          bytes: body,
          timeoutMs: computeChunkTimeoutMs(body.byteLength),
        }
      );
      if (patched.ok && patched.data) {
        digestRef.digest.update(body);
        session = {
          ...session,
          committedOffset: patched.data.offset,
          hashedBytes: digestRef.digest.hashedBytes,
          hashState: digestRef.digest.save(),
        };
        await ledger.putSession(session);
        continue;
      }
      if (patched.code === 'OFFSET_MISMATCH' && typeof patched.offset === 'number') {
        // The server knows best, always. Offsets only move forward, so
        // adopting converges instead of looping — and it costs no retry.
        const adopted = patched.offset;
        if (adopted < digestRef.digest.hashedBytes) {
          digestRef.digest = await StreamingDigest.create();
        }
        await digestRef.digest.catchUp(source, adopted);
        session = {
          ...session,
          committedOffset: adopted,
          hashedBytes: digestRef.digest.hashedBytes,
          hashState: digestRef.digest.save(),
        };
        await ledger.putSession(session);
        continue;
      }
      if (patched.code === 'UPLOAD_LOCKED') {
        await delay(2_000);
        continue;
      }
      if (patched.code === 'ALREADY_FINALIZED') {
        break; // the finalize picks up the receipt
      }
      if (patched.status === 404) {
        throw new StagedGoneError('upload session vanished mid-append');
      }
      this.assertOk(patched);
    }
    return session;
  }

  /**
   * Big clips: create-append-finalize against our own endpoints, one chunk at
   * a time, resuming at whatever offset the server states — including an
   * offset a head start put there before this send existed.
   */
  private async stageChunked(
    job: MediaJob,
    source: ChunkSource,
    initial: SessionRow
  ): Promise<string> {
    await this.setState(job.rows, 'uploading');
    const target: UploadTarget = {
      mediaId: job.mediaId,
      mediaType: job.rows[0].mediaType,
      hd: job.rows[0].hd ?? false,
      mime: await this.mimeFor(job),
      source,
    };

    const opened = await this.openSession(target, initial);
    if (opened.stagedId) {
      await this.setState(job.rows, 'uploaded');
      return opened.stagedId;
    }
    let session = opened.session;
    const digestRef = { digest: await this.digestFor(session, source) };

    let cycles = 0;
    for (;;) {
      session = await this.pumpChunks(target, session, digestRef);

      // FINALIZE — the moment the staged row (and its one-hour clock) comes
      // into being, so it happens here, with the destination POST next. A head
      // start never reaches this line: finalize is welded to a send.
      await this.setState(job.rows, 'uploaded');
      await digestRef.digest.catchUp(source, source.size);
      await this.uploadBucket.take();
      const finalized = await this.request<{ staged_id: string }>(
        'POST',
        `/media/uploads/${session.uploadId}/finalize`,
        {
          json: { total_bytes: source.size, checksum_sha256: digestRef.digest.hex() },
        }
      );
      if (finalized.ok && finalized.data) {
        session = {
          ...session,
          stagedId: finalized.data.staged_id,
          stagedAt: Date.now(),
        };
        await ledger.putSession(session);
        return finalized.data.staged_id;
      }
      if (finalized.code === 'LENGTH_MISMATCH' && cycles++ < 2) {
        // A chunk we believed landed did not. Re-sync and top up.
        const meta = await this.request<{ offset: number }>(
          'GET',
          `/media/uploads/${session.uploadId}`
        );
        this.assertOk(meta);
        const offset = meta.data!.offset;
        if (offset < digestRef.digest.hashedBytes) {
          digestRef.digest = await StreamingDigest.create();
        }
        await digestRef.digest.catchUp(source, offset);
        session = { ...session, committedOffset: offset };
        continue;
      }
      if (finalized.status === 404) {
        throw new StagedGoneError('upload session vanished before finalize');
      }
      if (finalized.code === 'CHECKSUM_MISMATCH') {
        // The server destroyed the session; the bytes here are still good, so
        // this is a retry from zero, not a refusal.
        await ledger.removeSession(job.mediaId);
        throw new SendError('Upload was corrupted in transit', undefined, 'CHECKSUM_MISMATCH');
      }
      this.assertOk(finalized);
    }
  }

  /** One destination at a time, in the order they were committed. */
  private async deliver(job: MediaJob, stagedId: string | null): Promise<void> {
    let staged = stagedId;
    let claimed = false;
    let reuploaded = false;

    for (let i = 0; i < job.rows.length; i++) {
      const row = ledger.get(job.rows[i].id);
      if (!row || row.state === 'done' || row.state === 'stuck') continue;
      // Persisted before it is acted on — a kill mid-POST leaves a row that
      // says "sending", and the retry rides the same client key.
      await ledger.patch(row.id, { state: 'sending', pausedAuth: false });

      // Whoever is last CLAIMS the staged row (free, no bytes copied); anyone
      // with destinations still behind them COPIES it. Getting this backwards
      // would let a claim vacuum the row out from under a copy still to come.
      const remaining = job.rows
        .slice(i + 1)
        .filter((r) => ledger.get(r.id)?.state !== 'stuck' && ledger.get(r.id));
      const keepStaged = remaining.length > 0;

      try {
        const server = await this.post(row, staged, keepStaged);
        claimed = claimed || (!!staged && !keepStaged && !isVault(row));
        await this.settle(row, server);
      } catch (err) {
        if (err instanceof StagedGoneError && staged && !reuploaded) {
          // The staged row expired or was claimed out from under us. The bytes
          // never left this phone — upload them again, once, and carry on.
          reuploaded = true;
          await ledger.removeSession(job.mediaId);
          staged = await this.prepareMedia({ ...job, rows: [row] });
          i -= 1;
          continue;
        }
        throw err;
      }
    }

    // Nobody consumed the staged row (every destination copied, or the last one
    // was a Memories save — those always copy). Hand the disk back now instead
    // of leaving it to the hourly reaper.
    if (staged && !claimed) {
      void this.request('DELETE', `/media/staged/${staged}`).catch(() => {});
    }
    await this.finishMedia(job.mediaId);
  }

  private async post(
    row: QueueRow,
    stagedId: string | null,
    keepStaged: boolean
  ): Promise<unknown> {
    const form = new FormData();
    form.append('media_type', row.mediaType);
    if (row.caption) form.append('caption', row.caption);
    const overlays = serializeOverlays(row.overlays);
    if (overlays) form.append('overlays', overlays);
    if (row.hd) form.append('hd', 'true');
    if (row.facing) form.append('facing', row.facing);
    form.append('client_key', row.clientKey);

    if (row.savedItemId) {
      form.append('saved_item_id', String(row.savedItemId));
    } else if (stagedId) {
      form.append('staged_media_id', stagedId);
      // Memories always copies server-side, so the flag would be noise there —
      // and /vault/media does not accept it.
      if (keepStaged && !isVault(row)) form.append('keep_staged', 'true');
    } else {
      throw new SendError('Nothing to send for this moment', 410, 'MEDIA_GONE', true);
    }

    let endpoint: string;
    const dest = row.dest;
    switch (dest.kind) {
      case 'friend':
        endpoint = '/messages';
        form.append('recipient_id', String(dest.friendId));
        form.append('retention_type', row.retentionType || '24h');
        form.append('replay_allowed', String(row.replayAllowed ?? true));
        break;
      case 'group':
        endpoint = `/groups/${dest.groupId}/messages`;
        break;
      case 'story':
        endpoint = '/stories';
        form.append('audience', dest.audience);
        if (dest.viewerIds?.length) {
          form.append('private_viewer_ids', dest.viewerIds.join(','));
        }
        if (dest.batchId !== undefined && dest.batchPosition !== undefined) {
          form.append('batch_id', dest.batchId);
          form.append('batch_position', String(dest.batchPosition));
        }
        break;
      case 'vault':
        endpoint = '/vault/media';
        form.append('is_private', String(dest.isPrivate ?? false));
        if (dest.albumId) form.append('album_id', String(dest.albumId));
        break;
    }

    const reply = await this.request<unknown>('POST', endpoint, {
      body: form,
      timeoutMs: computeUploadTimeoutMs(row.size),
    });
    if (!reply.ok && reply.status === 404 && stagedId && !row.savedItemId) {
      throw new StagedGoneError(reply.message ?? 'Staged media not found');
    }
    this.assertOk(reply);
    return reply.data;
  }

  /** A destination landed: the row is done, the bubble is told, and when the
   * last row of a draft lands the draft is finished with. */
  private async settle(row: QueueRow, server: unknown): Promise<void> {
    await ledger.remove([row.id]);
    this.fireResult({ success: true, row, server });
    if (row.draftId && ledger.byMedia(row.mediaId).length === 0) {
      void drafts.remove([row.draftId], 'delivered');
    }
    this.refreshState();
  }

  /** The last row for a media is gone — release the upload session, and the
   * media itself if no draft is keeping it. */
  private async finishMedia(mediaId: string): Promise<void> {
    if (ledger.byMedia(mediaId).length > 0) return;
    await ledger.removeSession(mediaId);
    const meta = await mediaStore.meta(mediaId);
    if (meta && !meta.draft) await mediaStore.remove([mediaId], 'delivered');
  }

  // ---- failure ----

  private async noteFailure(job: MediaJob, err: unknown): Promise<void> {
    const error = err instanceof Error ? err : new Error(String(err));
    const status = err instanceof SendError ? err.status : undefined;

    if (status === 401) {
      // Signed out, not refused (BUG-069). No retry is burned, no failure is
      // shown, and the pass stops — every other row would 401 too.
      console.warn('[Queue] paused — session expired');
      this.needsAuth = true;
      for (const row of job.rows) {
        await ledger.patch(row.id, { pausedAuth: true, state: 'queued' });
      }
      reportUnauthorized();
      this.refreshState();
      return;
    }

    const permanent =
      (err instanceof SendError && err.permanent) || isPermanentStatus(status);
    for (const row of job.rows) {
      const current = ledger.get(row.id);
      if (!current || current.state === 'done') continue;
      const attempts = current.attempts + 1;
      if (permanent) {
        // The server understood and said no, or the bytes are gone. Retrying
        // could only fail identically — but the moment is still on the phone,
        // so it becomes a decision rather than a deletion.
        await ledger.patch(row.id, {
          state: 'stuck',
          attempts,
          lastError: error.message,
          stuckReason: error.message,
        });
        this.fireResult({ success: false, row: current, error: error.message, permanent: true });
        continue;
      }
      const stuck = attempts >= STUCK_AFTER_FAILURES;
      await ledger.patch(row.id, {
        state: stuck ? 'stuck' : 'queued',
        attempts,
        lastError: error.message,
        stuckReason: stuck ? error.message : undefined,
        nextAttemptAt: stuck ? undefined : Date.now() + retryDelayMs(attempts),
      });
      if (stuck) {
        this.fireResult({ success: false, row: current, error: error.message, permanent: false });
      }
    }
    console.error('[Queue] attempt failed:', error.message);
    this.refreshState();
  }

  /** Arm one timer for the next automatic attempt — the weak-but-online fix.
   * Without it a queue only ever retried when the 'online' event fired, which
   * on a phone with one bar it never does. */
  private scheduleRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (this.needsAuth) return; // a timer would just 401 on a loop
    const waiting = ledger
      .all()
      .filter((row) => row.state !== 'stuck' && row.state !== 'done');
    if (waiting.length === 0) return;
    const soonest = Math.min(
      ...waiting.map((row) => row.nextAttemptAt ?? Date.now())
    );
    this.retryTimer = setTimeout(
      () => {
        this.retryTimer = null;
        this.kick();
      },
      Math.max(soonest - Date.now(), 1_000)
    );
  }

  // ---- reading the bytes ----

  private async sourceFor(job: MediaJob): Promise<ChunkSource> {
    const row = job.rows[0];
    if (row.legacyRowId) {
      // A record the evacuation could not thin. Its bytes are read ONCE, here,
      // at its upload turn — never at boot, which is what made them fatal.
      const blob = await legacyOutboxBlob(row.legacyRowId);
      if (blob) return blobSource(blob);
    }
    const meta = await mediaStore.meta(row.mediaId);
    if (!meta) {
      // Deliberately NOT permanent. The shutter writes the capture un-awaited,
      // so a send tapped a heartbeat later can genuinely arrive first; a moment
      // that is merely early must not be condemned. Five backoffs later it is
      // stuck and visible, which is the honest end for one that never appears.
      throw new SendError('That moment is still being saved', undefined, 'MEDIA_PENDING');
    }
    return mediaSource(row.mediaId, meta.size);
  }

  private async mimeFor(job: MediaJob): Promise<string> {
    const meta = await mediaStore.meta(job.rows[0].mediaId);
    return meta?.mimeType || (job.rows[0].mediaType === 'video' ? 'video/mp4' : 'image/jpeg');
  }

  /** The whole blob, for the small-media POST only. Assembled from the same
   * chunk rows, and only ever under HEAVY_BYTES. */
  private async wholeBlob(job: MediaJob, source: ChunkSource): Promise<Blob> {
    const row = job.rows[0];
    if (row.legacyRowId) {
      const pieces: BlobPart[] = [];
      for (let offset = 0; offset < source.size; offset += MEDIA_CHUNK_BYTES) {
        const piece = await source.readAt(offset);
        if (!piece) break;
        pieces.push(piece as BlobPart);
      }
      return new Blob(pieces);
    }
    const blob = await mediaStore.assembleBlob(row.mediaId);
    if (!blob) {
      throw new SendError('Media unreadable on this device', 410, 'MEDIA_GONE', true);
    }
    return blob;
  }

  // ---- transport ----

  private assertOk(reply: Reply<unknown>): void {
    if (reply.ok) return;
    throw new SendError(
      reply.message || 'Send failed',
      reply.status,
      reply.code,
      isPermanentStatus(reply.status)
    );
  }

  /**
   * One request. Its own transport rather than the shared api client, because
   * pacing needs the Retry-After header and a chunk needs a raw body — and
   * because a 429 must be able to come back as "wait", not as a failure.
   */
  private async request<T>(
    method: string,
    path: string,
    opts: {
      json?: unknown;
      body?: FormData;
      bytes?: Uint8Array;
      timeoutMs?: number;
    } = {}
  ): Promise<Reply<T>> {
    const wait = this.pacedUntil - Date.now();
    if (wait > 0) await delay(wait);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? 60_000);
    try {
      const headers: Record<string, string> = {};
      let body: BodyInit | undefined;
      if (opts.json !== undefined) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(opts.json);
      } else if (opts.bytes) {
        headers['Content-Type'] = 'application/octet-stream';
        body = new Blob([opts.bytes as BlobPart]);
      } else if (opts.body) {
        body = opts.body;
      }

      const response = await fetch(`${API_BASE_URL}${path}`, {
        method,
        credentials: 'include',
        headers,
        body,
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok) {
        return { ok: true, status: response.status, data: payload.data as T };
      }
      if (response.status === 429) {
        // Pacing, never a burned retry: the server is telling us when, and the
        // only correct response is to wait that long.
        const header = Number(response.headers?.get?.('Retry-After') ?? '');
        const retryAfterMs =
          Number.isFinite(header) && header >= 0 ? Math.max(header * 1000, 250) : 5_000;
        this.pacedUntil = Date.now() + retryAfterMs;
        await delay(retryAfterMs);
        return this.request<T>(method, path, opts);
      }
      const detail = payload.detail ?? payload.error ?? {};
      return {
        ok: false,
        status: response.status,
        code: typeof detail === 'object' ? detail.code : undefined,
        message:
          (typeof detail === 'object' ? detail.message : detail) ||
          payload.message ||
          `Request failed with status ${response.status}`,
        offset: typeof detail === 'object' ? detail.offset : undefined,
      };
    } catch (err) {
      const aborted = err instanceof Error && err.name === 'AbortError';
      return {
        ok: false,
        status: 0,
        code: aborted ? 'TIMEOUT' : 'NETWORK_ERROR',
        message: aborted ? 'Timed out' : 'Network error',
      };
    } finally {
      clearTimeout(timer);
    }
  }

  // ---- the user's handles on a stuck queue ----

  async retryStuck(kind: Kind = 'all'): Promise<void> {
    await this.init();
    await this.retryRows(
      ledger.all().filter((row) => row.state === 'stuck' && matchesKind(row, kind))
    );
  }

  /** Try these rows again NOW — the send-queue sheet's per-row handle. Clears
   * the backoff as well as the stuck flag: "try again" that then waits four
   * minutes is not what the words say. */
  async retryKeys(keys: string[]): Promise<void> {
    await this.init();
    const wanted = new Set(keys);
    await this.retryRows(ledger.all().filter((row) => wanted.has(row.clientKey)));
  }

  private async retryRows(rows: QueueRow[]): Promise<void> {
    for (const row of rows) {
      await ledger.patch(row.id, {
        state: 'queued',
        attempts: 0,
        stuckReason: undefined,
        nextAttemptAt: undefined,
      });
    }
    this.refreshState();
    this.kick();
  }

  /** The user gives up on a stuck moment. */
  async discardStuck(kind: Kind = 'all'): Promise<void> {
    await this.init();
    await this.discardRows(
      ledger.all().filter((row) => row.state === 'stuck' && matchesKind(row, kind))
    );
  }

  /** The user gives up on one row of the send-queue sheet — which may be one
   * destination of a moment that is still going somewhere else. */
  async discardKeys(keys: string[]): Promise<void> {
    await this.init();
    const wanted = new Set(keys);
    await this.discardRows(ledger.all().filter((row) => wanted.has(row.clientKey)));
  }

  /** The ROWS go; the moment does not — its draft (or its media) is still on
   * the phone and resurfaces as an unsent moment. */
  private async discardRows(doomed: QueueRow[]): Promise<void> {
    if (doomed.length === 0) return;
    await ledger.remove(doomed.map((row) => row.id));
    for (const row of doomed) {
      // The key is dead, so it comes off the draft: reconcile only clears a
      // draft whose keys ALL landed, and an unanswerable key is what makes a
      // delivered moment immortal (BUG-075).
      if (row.draftId) void drafts.unstampKeys(row.draftId, [row.clientKey]);
      this.fireResult({ success: false, row, error: 'Discarded', permanent: true });
      if (ledger.byMedia(row.mediaId).length === 0) {
        await ledger.removeSession(row.mediaId);
        // Giving up on the SEND is not giving up on the moment. If nothing else
        // owns these bytes they become an unsent capture, visible in the drafts
        // sheet — his to send again or delete, never quietly swept.
        await mediaStore.updateMeta(row.mediaId, (meta) => ({ ...meta, draft: true }));
      }
    }
    this.refreshState();
  }

  /** These keys are already on the server (the launch reconcile) — drop their
   * rows so the queue can't send something that has landed. */
  async dropByClientKeys(keys: string[]): Promise<void> {
    if (keys.length === 0) return;
    await this.init();
    const landed = new Set(keys);
    const doomed = ledger.all().filter((row) => landed.has(row.clientKey));
    await ledger.remove(doomed.map((row) => row.id));
    for (const row of doomed) await this.finishMedia(row.mediaId);
    this.refreshState();
  }

  /** Media the queue is delivering right now — draft surfaces hide these so a
   * just-sent moment never masquerades as unsent. Synchronous by design: the
   * ledger is mirrored in memory precisely so this question costs nothing. */
  activeMediaIds(): Set<string> {
    return ledger.activeMediaIds();
  }

  // ---- what the surfaces read ----

  private async setState(rows: QueueRow[], state: QueueRow['state']): Promise<void> {
    for (const row of rows) {
      if (ledger.get(row.id)) await ledger.patch(row.id, { state });
    }
    this.refreshState();
  }

  private refreshState(): void {
    const rows = ledger.all();
    const sends = rows.filter((row) => !isVault(row));
    const saves = rows.filter(isVault);
    const stuck = sends.filter((row) => row.state === 'stuck').length;
    const savingStuck = saves.filter((row) => row.state === 'stuck').length;
    const pending = sends.length - stuck;
    this.state = {
      pending,
      stuck,
      offline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
      // Only claim a sign-in is needed while something is actually waiting on
      // one — a lone stuck row must not read "finish sending 0 moments".
      needsAuth: this.needsAuth && pending + saves.length - savingStuck > 0,
      saving: saves.length - savingStuck,
      savingStuck,
    };
    for (const cb of this.stateCallbacks) cb(this.state);
  }

  /** Fires immediately on subscribe (surfaces render current state first). */
  onState(cb: (state: SendState) => void): () => void {
    this.stateCallbacks.add(cb);
    cb(this.state);
    return () => this.stateCallbacks.delete(cb);
  }

  onResult(cb: (result: SendResult) => void): () => void {
    this.resultCallbacks.add(cb);
    return () => this.resultCallbacks.delete(cb);
  }

  /** Any change to the ledger (rows added, states moved, rows gone). Fires
   * immediately, like everything else the surfaces subscribe to. */
  onChange(cb: () => void): () => void {
    return ledger.onChange(cb);
  }

  /** Upload progress moving — a separate, much noisier signal than `onChange`,
   * and deliberately not folded into it: an offset lands every ~2 MiB, and
   * `onChange`'s subscribers walk the whole metadata store. */
  onProgress(cb: () => void): () => void {
    return ledger.onUploadChange(cb);
  }

  /**
   * How far the bytes have actually got, in bytes the SERVER confirmed. Head
   * starts included — they are uploads too. Nothing here is estimated: a
   * percentage this app invents is a percentage that lies (BUG-058).
   */
  uploadProgress(): { sent: number; total: number } {
    let sent = 0;
    let total = 0;
    for (const session of ledger.sessions()) {
      if (session.totalBytes <= 0) continue;
      total += session.totalBytes;
      sent += session.stagedId
        ? session.totalBytes
        : Math.min(session.committedOffset, session.totalBytes);
    }
    return { sent, total };
  }

  /** The progress of ONE moment, for a row of the send-queue sheet. */
  mediaProgress(mediaId: string): { sent: number; total: number } | null {
    const session = ledger.sessions().find((row) => row.mediaId === mediaId);
    if (!session || session.totalBytes <= 0) return null;
    return {
      sent: session.stagedId
        ? session.totalBytes
        : Math.min(session.committedOffset, session.totalBytes),
      total: session.totalBytes,
    };
  }

  /** Every row the queue is still carrying, for the send-queue sheet. */
  rows(): QueueRow[] {
    return ledger.all();
  }

  /** Tests only. */
  close(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.ready = null;
    this.draining = false;
    this.rerun = false;
    this.needsAuth = false;
    this.pacedUntil = 0;
    this.passes = 0;
    this.running = null;
    this.abandoning.clear();
    this.inFlight.clear();
    this.stateCallbacks.clear();
    this.resultCallbacks.clear();
    this.listening = false;
  }

  private fireResult(result: SendResult): void {
    for (const cb of this.resultCallbacks) cb(result);
  }
}

export const sendEngine = new SendEngineService();

// Loads the ledger at import — metadata only, no network, no bytes. Tests own
// their instances.
if (import.meta.env.MODE !== 'test') {
  sendEngine.init().catch((err) => console.error('[Queue] init failed:', err));
}
